import { AnimatePresence } from 'framer-motion'
import parse from 'html-react-parser'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import FieldQty from '@/components/FieldQty'
import Price from '@/components/Price'
import useCheckout from '@/features/Checkout/hooks/useCheckout'
import type { Product, ProductConsumerCategory } from '@/lib/api.types'
import type { TicketPrice } from '@/types/tickets'

import LockedTicketModal from '../LockedTicketModal'
import {
  TicketCardHeader,
  TicketCardOldPrice,
  TicketCardPrice,
  TicketCardQty,
  TicketCardWrapper,
  TicketUnlockBtn
} from './TicketCard.styles'

const normalizePrice = (value: unknown) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return 0
  }

  return numericValue >= 0 ? numericValue : 0
}

type ProductConsumerCategoryWithPricing = ProductConsumerCategory & {
  lowestPriceIn30Days?: {
    grossAmount?: string
  }
}

const extractTicketPrice = (
  product: Product,
  consumerCategory: ProductConsumerCategoryWithPricing | null,
  fallbackCurrency?: string
): TicketPrice => {
  const primaryCategory = (consumerCategory ?? product.consumerCategories?.[0]) as
    | {
        price?: {
          grossAmount?: string
          currency?: string
          taxPercentage?: number
          tax?: {
            value?: string
          }
        }
        lowestPriceIn30Days?: {
          grossAmount?: string
        }
      }
    | undefined

  const grossAmount = normalizePrice(primaryCategory?.price?.grossAmount ?? product.price?.grossAmount ?? product.defaultPrice)
  const lowestGrossAmountIn30Days = normalizePrice(
    primaryCategory?.lowestPriceIn30Days?.grossAmount ?? primaryCategory?.price?.grossAmount ?? grossAmount
  )
  const taxPercentage = normalizePrice(
    primaryCategory?.price?.tax?.value ?? primaryCategory?.price?.taxPercentage ?? product.price?.taxPercentage
  )
  const currency = primaryCategory?.price?.currency || product.price?.currency || fallbackCurrency || 'PLN'

  return {
    grossAmount,
    currency,
    taxPercentage,
    lowestGrossAmountIn30Days
  }
}

const padDatePart = (value: number) => String(value).padStart(2, '0')
const getTodayDate = () => {
  const now = new Date()
  return `${now.getFullYear()}-${padDatePart(now.getMonth() + 1)}-${padDatePart(now.getDate())}`
}

const getStringConfig = (product: Product, key: string) => {
  const value = product.additionalConfiguration?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

const getAllowStandalonePurchase = (category: ProductConsumerCategory | null | undefined) => {
  if (!category) {
    return true
  }

  const categoryRecord = category as unknown as { settings?: unknown }
  const settings = categoryRecord.settings
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return true
  }

  const allowStandalonePurchase = (settings as { allowStandalonePurchase?: unknown }).allowStandalonePurchase
  if (typeof allowStandalonePurchase === 'boolean') {
    return allowStandalonePurchase
  }

  return true
}

interface TicketCardProps {
  product: Product
  consumerCategory?: ProductConsumerCategory | null
  vertical?: boolean
  currency?: string
}

const TicketCard = ({ product, consumerCategory, vertical, currency: currencyFallback }: TicketCardProps) => {
  const productUnlockId = Number(product.productId || product.id)
  const productId = String(product.id)
  const cartItemId = consumerCategory?.id
    ? `ticket:${productId}:${consumerCategory.id}`
    : `ticket:${String(product.productId || product.id)}`
  const name = consumerCategory?.name || product.name
  const description = consumerCategory?.description || product.description
  const coverImageUrl = product.coverImage?.url || product.images?.[0]?.url || '/img/gallery01.jpg'
  const restricted = Boolean(product.requirePersonalData)
  const price = extractTicketPrice(product, (consumerCategory || null) as ProductConsumerCategoryWithPricing | null, currencyFallback)
  const { lowestGrossAmountIn30Days, grossAmount, currency } = price
  const consumerCategoryId = consumerCategory?.id ?? product.consumerCategories?.[0]?.id ?? null
  const date = getStringConfig(product, 'date') ?? getTodayDate()
  const startTime = getStringConfig(product, 'startTime')
  const paymentMethod = product.defaultPaymentMethod || 'onSite'
  const { t } = useTranslation()
  const { addToCart, removeFromCart, cart, setQty, addUnlockedTicket, unlockedTickets, cartNotRestrictedQty } = useCheckout()
  const cartItem = useMemo(() => cart.find(o => o.id === cartItemId) || null, [cart, cartItemId])
  const qty = cartItem?.qty || 0
  const existsInCart = qty > 0
  const allowStandalonePurchase = useMemo(() => getAllowStandalonePurchase(consumerCategory), [consumerCategory])
  const productConsumerCategoryIds = useMemo(
    () => new Set((product.consumerCategories || []).map(category => String(category.id))),
    [product.consumerCategories]
  )
  const otherConsumerCategoriesQty = useMemo(
    () =>
      cart.reduce((sum, item) => {
        if (item.productId !== productId) {
          return sum
        }
        if (!item.consumerCategoryId) {
          return sum
        }

        const itemCategoryId = String(item.consumerCategoryId)
        if (!productConsumerCategoryIds.has(itemCategoryId)) {
          return sum
        }
        if (itemCategoryId === String(consumerCategoryId)) {
          return sum
        }

        return sum + Math.max(0, Number(item.qty) || 0)
      }, 0),
    [cart, consumerCategoryId, productConsumerCategoryIds, productId]
  )
  const standaloneBlocked = !allowStandalonePurchase && otherConsumerCategoriesQty <= 0
  const disablePlus = standaloneBlocked
  const disableMinus = qty <= 0
  const [showLockedModal, setShowLockedModal] = useState(false)
  const isUnlocked = useMemo(
    () => unlockedTickets.includes(productUnlockId) || cartNotRestrictedQty > 0,
    [unlockedTickets, productUnlockId, cartNotRestrictedQty]
  )
  const isLocked = !isUnlocked && restricted

  useEffect(() => {
    if (!allowStandalonePurchase && standaloneBlocked && qty > 0) {
      setQty({ id: cartItemId, qty: 0 })
    }
  }, [allowStandalonePurchase, cartItemId, qty, setQty, standaloneBlocked])

  const handleQtyChange = (number: number) => {
    if (isLocked) {
      toast.error(t('ticketLocked'))
      return
    }
    if (number > qty && standaloneBlocked) {
      toast.error(t('cantAddToCart'))
      return
    }
    if (number > 0) {
      if (number === 1 && !existsInCart) {
        if (!unlockedTickets.includes(productUnlockId) && restricted && cartNotRestrictedQty <= 0) {
          toast.error(t('cantAddToCart'))
          return
        } else {
          addToCart({
            id: cartItemId,
            productId,
            productName: product.name,
            coverImageUrl,
            name,
            price,
            qty: number,
            consumerCategoryId,
            date,
            startTime,
            pickupMethod: 'onSite',
            paymentMethod,
            orderItemComponents: product.productComponents ?? [],
            unlockId: productUnlockId,
            restricted
          })
        }
      } else {
        setQty({ id: cartItemId, qty: number })
      }
    } else if (number === 0) {
      removeFromCart({ id: cartItemId })
    }
  }

  const handleUnlock = () => {
    setShowLockedModal(false)
    addUnlockedTicket(productUnlockId)
  }

  return (
    <>
      <TicketCardWrapper vertical={vertical}>
        <TicketCardHeader>
          <h4>{name}</h4>
          {description && <p>{parse(description)}</p>}
        </TicketCardHeader>
        <TicketCardPrice>
          <Price currency={currency}>{grossAmount}</Price>
          <TicketCardOldPrice>
            {t('lowest30DaysPrice', {
              price: `${Number(lowestGrossAmountIn30Days)?.toFixed(2)} ${currency}`
            })}
          </TicketCardOldPrice>
        </TicketCardPrice>
        {!isLocked && (
          <TicketCardQty>
            <FieldQty value={qty} onChange={handleQtyChange} disablePlus={disablePlus} disableMinus={disableMinus} />
          </TicketCardQty>
        )}
        {isLocked && <TicketUnlockBtn onClick={() => setShowLockedModal(true)}>{t('unlock')}</TicketUnlockBtn>}
      </TicketCardWrapper>
      <AnimatePresence>
        {showLockedModal && <LockedTicketModal onClose={() => setShowLockedModal(false)} onSuccess={handleUnlock} />}
      </AnimatePresence>
    </>
  )
}

export default TicketCard
